/**
 * STORAGE UPGRADE MODAL
 * 
 * Modal showing pricing tiers and allowing users to upgrade their plan.
 * 
 * Features:
 * - All tier comparison
 * - Monthly vs yearly toggle
 * - Current tier highlight
 * - Feature comparison
 * - Stripe checkout integration (placeholder for now)
 */

import { useState } from 'react';
import { X, Check, Sparkles, TrendingUp, Shield, Zap } from 'lucide-react';
import { 
  getAllTiers, 
  calculateYearlySavings,
  formatStorageLimit,
  type StorageTier,
  type StorageTierId 
} from '../constants/storageTiers';
import { useStorageUsage } from '../hooks/useStorageUsage';

interface StorageUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPlan?: (tierId: StorageTierId, billingCycle: 'monthly' | 'yearly') => void;
  highlightTier?: StorageTierId;
}

export default function StorageUpgradeModal({ 
  isOpen, 
  onClose, 
  onSelectPlan,
  highlightTier 
}: StorageUpgradeModalProps) {
  const { tierId: currentTierId, usedFormatted } = useStorageUsage();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedTier, setSelectedTier] = useState<StorageTierId | null>(highlightTier || null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const tiers = getAllTiers();
  
  if (!isOpen) return null;
  
  const handleSelectPlan = async (tier: StorageTier) => {
    if (tier.id === currentTierId) return;
    if (tier.id === 'free') return; // Can't "buy" free tier
    
    setSelectedTier(tier.id);
    setIsProcessing(true);
    
    try {
      // Call the onSelectPlan callback which will handle Stripe checkout
      onSelectPlan?.(tier.id, billingCycle);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const getButtonText = (tier: StorageTier) => {
    if (tier.id === currentTierId) return 'Current Plan';
    if (tier.id === 'free') return 'Free';
    if (tier.priceMonthly === 0) return 'Get Started';
    
    const currentIndex = tiers.findIndex(t => t.id === currentTierId);
    const tierIndex = tiers.findIndex(t => t.id === tier.id);
    
    if (tierIndex < currentIndex) return 'Downgrade';
    return 'Upgrade';
  };
  
  const getPrice = (tier: StorageTier) => {
    if (tier.priceMonthly === 0) return 'Free';
    
    if (billingCycle === 'yearly') {
      const monthlyEquivalent = (tier.priceYearly / 12).toFixed(2);
      return `$${monthlyEquivalent}/mo`;
    }
    
    return `$${tier.priceMonthly}/mo`;
  };
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="glass-modal max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800/95 backdrop-blur-sm px-6 py-4 border-b border-slate-600 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-glass font-heading">
              Upgrade Your Storage
            </h2>
            <p className="text-glass-secondary font-body">
              Choose the plan that fits your collection
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-default"
          >
            <X className="w-5 h-5 text-glass-muted" />
          </button>
        </div>
        
        {/* Billing toggle */}
        <div className="px-6 py-4 flex justify-center">
          <div className="inline-flex items-center bg-slate-800/60 rounded-full p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-default font-body ${
                billingCycle === 'monthly'
                  ? 'bg-slate-700 text-glass'
                  : 'text-glass-muted hover:text-glass'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-default font-body flex items-center gap-2 ${
                billingCycle === 'yearly'
                  ? 'bg-slate-700 text-glass'
                  : 'text-glass-muted hover:text-glass'
              }`}
            >
              Yearly
              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                Save up to 17%
              </span>
            </button>
          </div>
        </div>
        
        {/* Pricing cards */}
        <div className="px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {tiers.map((tier) => {
              const isCurrent = tier.id === currentTierId;
              const isRecommended = tier.recommended;
              const isHighlighted = tier.id === highlightTier;
              const savings = calculateYearlySavings(tier);
              
              return (
                <div
                  key={tier.id}
                  className={`relative rounded-xl border-2 transition-all ${
                    isRecommended || isHighlighted
                      ? 'border-purple-500 bg-purple-500/5'
                      : isCurrent
                        ? 'border-green-500 bg-green-500/5'
                        : 'border-slate-600 bg-slate-800/40 hover:border-slate-500'
                  }`}
                >
                  {/* Recommended badge */}
                  {isRecommended && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-1 bg-purple-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        POPULAR
                      </span>
                    </div>
                  )}
                  
                  {/* Current plan badge */}
                  {isCurrent && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                        CURRENT
                      </span>
                    </div>
                  )}
                  
                  <div className="p-5">
                    {/* Tier name and emoji */}
                    <div className="text-center mb-4">
                      <span className="text-3xl mb-2 block">{tier.emoji}</span>
                      <h3 className="text-lg font-bold text-glass font-body">{tier.name}</h3>
                      <p className="text-sm text-glass-muted font-body">
                        {formatStorageLimit(tier)}
                      </p>
                    </div>
                    
                    {/* Price */}
                    <div className="text-center mb-4">
                      <span className="text-3xl font-bold text-glass font-body">
                        {getPrice(tier)}
                      </span>
                      {billingCycle === 'yearly' && tier.priceYearly > 0 && (
                        <p className="text-xs text-green-400 mt-1 font-body">
                          Save ${savings}/year
                        </p>
                      )}
                    </div>
                    
                    {/* Features */}
                    <ul className="space-y-2 mb-6">
                      {tier.features.slice(0, 4).map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-glass-secondary font-body">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {/* CTA Button */}
                    <button
                      onClick={() => handleSelectPlan(tier)}
                      disabled={isCurrent || tier.id === 'free' || isProcessing}
                      className={`w-full py-2.5 rounded-lg font-medium transition-default font-body ${
                        isCurrent
                          ? 'bg-green-500/20 text-green-400 cursor-default'
                          : tier.id === 'free'
                            ? 'bg-slate-700/50 text-glass-muted cursor-default'
                            : isRecommended || isHighlighted
                              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
                              : 'bg-slate-700 text-glass hover:bg-slate-600'
                      }`}
                    >
                      {isProcessing && selectedTier === tier.id ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        getButtonText(tier)
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Trust badges */}
        <div className="px-6 pb-6">
          <div className="flex flex-wrap justify-center gap-6 py-4 border-t border-slate-600">
            <div className="flex items-center gap-2 text-sm text-glass-muted">
              <Shield className="w-4 h-4 text-green-400" />
              <span className="font-body">Secure payment via Stripe</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-glass-muted">
              <Zap className="w-4 h-4 text-yellow-400" />
              <span className="font-body">Instant upgrade</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-glass-muted">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              <span className="font-body">Cancel anytime</span>
            </div>
          </div>
        </div>
        
        {/* Current usage note */}
        <div className="px-6 pb-6">
          <div className="bg-slate-800/60 rounded-lg p-4 text-center">
            <p className="text-sm text-glass-secondary font-body">
              You're currently using <strong className="text-glass">{usedFormatted}</strong> of storage.
              {currentTierId === 'free' && (
                <span> Upgrade to unlock more space for your collection!</span>
              )}
            </p>
          </div>
        </div>
        
        {/* FAQ link */}
        <div className="px-6 pb-6 text-center">
          <p className="text-sm text-glass-muted font-body">
            Questions? Check our{' '}
            <a href="/faq" className="text-purple-400 hover:underline">FAQ</a>
            {' '}or{' '}
            <a href="/contact" className="text-purple-400 hover:underline">contact us</a>
          </p>
        </div>
      </div>
    </div>
  );
}
